import { useEffect, ReactNode } from "react";
import { useBranding } from "@/hooks/useBranding";

/**
 * Phase 6 — Applies partner brand colors + favicon + document title to the
 * live document. Direct workspaces (no partner_id) get the defaults already
 * baked into index.css and index.html, so this provider is a no-op for them.
 */
function hexToHsl(hex: string): string | null {
  const m = hex.trim().match(/^#?([a-f\d]{6}|[a-f\d]{3})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const light = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      case b: hue = (r - g) / d + 4; break;
    }
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
}

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const brand = useBranding();

  useEffect(() => {
    const root = document.documentElement;
    const touched: string[] = [];

    if (brand.isPartnerBranded && brand.primaryColor) {
      const hsl = hexToHsl(brand.primaryColor);
      if (hsl) {
        root.style.setProperty("--primary", hsl);
        root.style.setProperty("--sidebar-primary", hsl);
        root.style.setProperty("--sidebar-ring", hsl);
        root.style.setProperty("--ring", hsl);
        touched.push("--primary", "--sidebar-primary", "--sidebar-ring", "--ring");

        const parts = hsl.split(" ");
        const h = parts[0];
        const s = parts[1];
        const lNum = parseInt(parts[2].replace("%", ""), 10);
        const glowL = Math.min(95, lNum + 10);
        const glowHsl = `${h} ${s} ${glowL}%`;
        root.style.setProperty("--primary-glow", glowHsl);
        root.style.setProperty(
          "--gradient-primary",
          `linear-gradient(135deg, hsl(${hsl}) 0%, hsl(${glowHsl}) 100%)`
        );
        touched.push("--primary-glow", "--gradient-primary");

        // Derive the full brand-50…950 ramp from primary so legacy
        // `bg-brand-*` / `text-brand-*` utilities used on /auth and
        // marketing pages follow the partner color.
        const sNum = parseInt(s.replace("%", ""), 10);
        const ramp: Array<[string, number, number]> = [
          ["--brand-50", Math.min(100, sNum), 97],
          ["--brand-100", Math.min(100, sNum), 93],
          ["--brand-200", Math.min(100, sNum), 86],
          ["--brand-300", sNum, 76],
          ["--brand-400", sNum, 66],
          ["--brand-500", sNum, Math.max(50, lNum + 5)],
          ["--brand-600", sNum, lNum],
          ["--brand-700", sNum, Math.max(20, lNum - 8)],
          ["--brand-800", sNum, Math.max(15, lNum - 16)],
          ["--brand-900", sNum, Math.max(12, lNum - 23)],
          ["--brand-950", sNum, Math.max(8, lNum - 35)],
        ];
        ramp.forEach(([name, sat, light]) => {
          root.style.setProperty(name, `${h} ${sat}% ${light}%`);
          touched.push(name);
        });
        // Tie teal-accent to primary too so the auth-page accent gradient
        // doesn't clash with partner color.
        root.style.setProperty("--teal-accent-500", `${h} ${sNum}% ${Math.min(70, lNum + 10)}%`);
        root.style.setProperty("--teal-accent-600", `${h} ${sNum}% ${lNum}%`);
        touched.push("--teal-accent-500", "--teal-accent-600");
      }
    }

    if (brand.isPartnerBranded && brand.accentColor) {
      const hsl = hexToHsl(brand.accentColor);
      if (hsl) {
        root.style.setProperty("--accent", hsl);
        root.style.setProperty("--sidebar-accent", hsl);
        touched.push("--accent", "--sidebar-accent");
      }
    }

    // Document title
    const originalTitle = document.title;
    if (brand.isPartnerBranded) {
      document.title = brand.productName;
    }

    // Favicon
    let originalFaviconHref: string | null = null;
    let faviconEl: HTMLLinkElement | null = null;
    if (brand.isPartnerBranded && brand.faviconUrl) {
      faviconEl = document.querySelector("link[rel~='icon']");
      if (faviconEl) {
        originalFaviconHref = faviconEl.href;
        faviconEl.href = brand.faviconUrl;
      }
    }

    // OG / social-share meta tags. Update for partner-branded contexts so a
    // shared partner URL doesn't show WDC's preview to JS-executing crawlers.
    const metaOriginals: Array<{ el: HTMLMetaElement; attr: "content"; original: string }> = [];
    const metaCreated: HTMLMetaElement[] = [];
    const setMeta = (selector: string, value: string | null) => {
      if (!value) return;
      const el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) return;
      metaOriginals.push({ el, attr: "content", original: el.content });
      el.content = value;
    };
    const ensureMeta = (
      kind: "property" | "name",
      key: string,
      value: string | null,
    ) => {
      if (!value) return;
      const selector = `meta[${kind}="${key}"]`;
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (el) {
        metaOriginals.push({ el, attr: "content", original: el.content });
        el.content = value;
      } else {
        el = document.createElement("meta");
        el.setAttribute(kind, key);
        el.content = value;
        document.head.appendChild(el);
        metaCreated.push(el);
      }
    };
    if (brand.isPartnerBranded) {
      const ogTitle = brand.ogTitle || brand.productName;
      const ogDesc = brand.ogDescription;
      const ogImage = brand.ogImageUrl;
      setMeta('meta[property="og:title"]', ogTitle);
      setMeta('meta[name="description"]', ogDesc);
      setMeta('meta[property="og:description"]', ogDesc);
      setMeta('meta[property="og:image"]', ogImage);
      setMeta('meta[property="og:image:secure_url"]', ogImage);
      setMeta('meta[name="twitter:image"]', ogImage);
      if (ogImage) {
        ensureMeta("property", "og:image:width", "1200");
        ensureMeta("property", "og:image:height", "630");
      }
      if (typeof window !== "undefined") {
        setMeta('meta[property="og:url"]', window.location.href);
      }
    }


    return () => {
      touched.forEach((v) => root.style.removeProperty(v));
      if (brand.isPartnerBranded) document.title = originalTitle;
      if (faviconEl && originalFaviconHref !== null) faviconEl.href = originalFaviconHref;
      metaOriginals.forEach(({ el, original }) => { el.content = original; });
      metaCreated.forEach((el) => el.parentNode?.removeChild(el));
    };
  }, [
    brand.isPartnerBranded,
    brand.primaryColor,
    brand.accentColor,
    brand.productName,
    brand.faviconUrl,
    brand.ogTitle,
    brand.ogDescription,
    brand.ogImageUrl,
  ]);

  return <>{children}</>;
};
